import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  GripVertical,
  Plus,
  MoreVertical,
  Phone,
  Mail,
  Calendar,
  Building2,
  DollarSign,
  Clock,
  User,
  ChevronDown,
  Edit2,
  Trash2,
  Eye,
  Tag,
  MessageSquare
} from 'lucide-react';

// Kanban Card Component
const KanbanCard = ({ 
  deal, 
  onView, 
  onEdit, 
  onDelete,
  onQuickAction,
  isDragging 
}) => {
  const [showMenu, setShowMenu] = useState(false);
  
  const priorityColors = {
    low: 'border-l-slate-400',
    medium: 'border-l-blue-400',
    high: 'border-l-orange-400',
    urgent: 'border-l-red-400'
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -2 }}
      className={`
        bg-slate-800/80 rounded-lg p-3 border-l-4 ${priorityColors[deal.priority] || priorityColors.medium}
        cursor-pointer hover:bg-slate-700/80 transition-all group
        ${isDragging ? 'shadow-xl ring-2 ring-neon-cyan/50' : 'shadow-md'}
      `}
      onClick={() => onView(deal)}
      data-testid={`deal-card-${deal.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium text-sm truncate" title={deal.name}>
            {deal.name}
          </h4>
          {deal.company && (
            <div className="flex items-center text-xs text-slate-400 mt-0.5">
              <Building2 className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">{deal.company}</span>
            </div>
          )}
        </div>
        
        {/* Menu */}
        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1 rounded hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4 text-slate-400" />
          </button>
          
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="absolute right-0 top-6 w-32 bg-slate-700 rounded-lg shadow-xl border border-white/10 py-1 z-50"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => { onView(deal); setShowMenu(false); }}
                  className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center"
                >
                  <Eye className="w-3 h-3 mr-2" /> View
                </button>
                <button
                  onClick={() => { onEdit(deal); setShowMenu(false); }}
                  className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center"
                >
                  <Edit2 className="w-3 h-3 mr-2" /> Edit
                </button>
                <button
                  onClick={() => { onDelete(deal); setShowMenu(false); }}
                  className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center"
                >
                  <Trash2 className="w-3 h-3 mr-2" /> Delete
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Value */}
      {deal.value > 0 && (
        <div className="flex items-center text-neon-green text-sm font-medium mb-2">
          <DollarSign className="w-3 h-3 mr-0.5" />
          {deal.value.toLocaleString()}
          {deal.recurring && <span className="text-xs text-slate-400 ml-1">/mo</span>}
        </div>
      )}

      {/* Tags */}
      {deal.tags && deal.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {deal.tags.slice(0, 2).map((tag, idx) => (
            <span
              key={idx}
              className="px-1.5 py-0.5 bg-white/5 rounded text-xs text-slate-300"
            >
              {tag}
            </span>
          ))}
          {deal.tags.length > 2 && (
            <span className="px-1.5 py-0.5 text-xs text-slate-500">
              +{deal.tags.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <div className="flex items-center space-x-2">
          {/* Quick Actions */}
          <button
            onClick={(e) => { e.stopPropagation(); onQuickAction('call', deal); }}
            className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-neon-green transition-colors"
            title="Call"
          >
            <Phone className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onQuickAction('email', deal); }}
            className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-neon-cyan transition-colors"
            title="Email"
          >
            <Mail className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onQuickAction('schedule', deal); }}
            className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-neon-purple transition-colors"
            title="Schedule"
          >
            <Calendar className="w-3 h-3" />
          </button>
        </div>
        
        {/* Activity indicator */}
        {deal.last_activity && (
          <div className="flex items-center text-xs text-slate-500">
            <Clock className="w-3 h-3 mr-1" />
            {formatTimeAgo(deal.last_activity)}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Kanban Column Component
const KanbanColumn = ({ 
  stage, 
  deals, 
  onAddDeal, 
  onViewDeal, 
  onEditDeal, 
  onDeleteDeal,
  onQuickAction,
  onDragStart,
  onDragEnd,
  onDrop,
  isDropTarget
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  
  const columnValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const dealId = e.dataTransfer.getData('dealId');
    if (dealId) {
      onDrop(dealId, stage.id);
    }
  };

  return (
    <div
      className={`
        flex flex-col w-72 min-w-72 bg-slate-900/50 rounded-xl border transition-all
        ${isDragOver ? 'border-neon-cyan/50 bg-neon-cyan/5' : 'border-white/5'}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`stage-column-${stage.id}`}
    >
      {/* Column Header */}
      <div 
        className="p-3 border-b border-white/5"
        style={{ borderTopColor: stage.color, borderTopWidth: '3px', borderTopLeftRadius: '0.75rem', borderTopRightRadius: '0.75rem' }}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-2">
            <span className="text-white font-medium text-sm">{stage.label}</span>
            <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs text-slate-300">
              {deals.length}
            </span>
          </div>
          <button
            onClick={() => onAddDeal(stage.id)}
            className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-neon-cyan transition-colors"
            title="Add deal to this stage"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        
        {/* Column Total Value */}
        {columnValue > 0 && (
          <div className="text-xs text-slate-400">
            ${columnValue.toLocaleString()} total
          </div>
        )}
      </div>

      {/* Cards Container */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-320px)] custom-scrollbar">
        <AnimatePresence>
          {deals.map((deal) => (
            <div
              key={deal.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('dealId', deal.id);
                onDragStart(deal.id);
              }}
              onDragEnd={onDragEnd}
            >
              <KanbanCard
                deal={deal}
                onView={onViewDeal}
                onEdit={onEditDeal}
                onDelete={onDeleteDeal}
                onQuickAction={onQuickAction}
              />
            </div>
          ))}
        </AnimatePresence>
        
        {deals.length === 0 && (
          <div className="text-center py-8 text-slate-500 text-sm">
            <p>No deals</p>
            <button
              onClick={() => onAddDeal(stage.id)}
              className="mt-2 text-neon-cyan hover:text-neon-cyan/80 text-xs"
            >
              + Add a deal
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Kanban Board Component
export const KanbanBoard = ({
  stages,
  deals,
  onAddDeal,
  onViewDeal,
  onEditDeal,
  onDeleteDeal,
  onMoveDeal,
  onQuickAction
}) => {
  const [draggingId, setDraggingId] = useState(null);

  const handleDragStart = (dealId) => {
    setDraggingId(dealId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const handleDrop = (dealId, newStageId) => {
    onMoveDeal(dealId, newStageId);
    setDraggingId(null);
  };

  // Group deals by stage
  const dealsByStage = stages.reduce((acc, stage) => {
    acc[stage.id] = deals.filter(d => d.stage === stage.id);
    return acc;
  }, {});

  return (
    <div 
      className="flex space-x-4 overflow-x-auto pb-4 custom-scrollbar"
      data-testid="kanban-board"
    >
      {stages.map((stage) => (
        <KanbanColumn
          key={stage.id}
          stage={stage}
          deals={dealsByStage[stage.id] || []}
          onAddDeal={onAddDeal}
          onViewDeal={onViewDeal}
          onEditDeal={onEditDeal}
          onDeleteDeal={onDeleteDeal}
          onQuickAction={onQuickAction}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          isDropTarget={draggingId !== null}
        />
      ))}
    </div>
  );
};

// Helper function
function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}

export default KanbanBoard;
